<template>
  <div>
    <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet" />
    <div class="header-row">
      <div class="breadcrumb-wrapper">
        <BreadCrumb :companyName="selectedGroupDescription" @click="goBackWithConfirmation" />
      </div>
    </div>

    <div class="tabs-and-summary-row">
      <div class="tabs-wrapper">
        <TabsNavigation
          :tabs="navigationTabs"
          :active-tab="activeTab"
          @tab-change="handleTabChange"
        />
      </div>
      <div v-if="activeTab !== 'dashboard'" class="summary-pills-container">
        <SummaryPills
          ref="summaryPillsRef"
          :filtered-table-data="filteredTableData"
          :available-agents="availableAgents"
          :selected-agent="selectedAgent"
        />
        <div v-if="activeTab === 'altas'" class="inconsistencies-section">
          <div class="summary-bar">
            <span
              :class="{
                'inconsistencies-text-bold': inconsistenciesCount > 0,
                'inconsistencies-text-normal': inconsistenciesCount === 0,
              }"
            >
              Mostrar solo inconsistencias (
              <span
                :class="{
                  'red-text': inconsistenciesCount > 0,
                  'black-text': inconsistenciesCount === 0,
                }"
                >{{ inconsistenciesCount }}</span
              >
              )
            </span>
          </div>
          <div class="toggle-button">
            <label class="switch">
              <input
                type="checkbox"
                v-model="showOnlyInconsistencies"
                :disabled="inconsistenciesCount === 0"
              />
              <span class="slider round"></span>
            </label>
          </div>
        </div>
      </div>
    </div>

    <div v-if="activeTab === 'altas'" class="employee-table-container">
      <EmployeeTable
        :table-data="tableData"
        :show-only-inconsistencies="showOnlyInconsistencies"
        :is-row-registered="isRowRegistered"
        :re-validate-data="reValidateData"
        :confirmation-before-remove-row="confirmationBeforeRemoveRow"
        :get-row-status="getRowStatus"
        :get-field-errors="getFieldErrors"
        :use-producto-input="false"
        :cabeceras="getCabeceras()"
        :save-updated-field="saveUpdatedField"
      />

      <div class="btn-wrapper-row">
        <div class="left-buttons">
          <div class="register-actions-container">
            <button
              class="register-actions-button"
              @click="toggleRegisterActionsDropdown"
              :class="{ active: showRegisterActionsDropdown }"
            >
              <span class="button-text">Acciones de registro</span>
              <div class="icon-container">
                <div class="separator-line"></div>
                <img :src="downArrowIcon" alt="dropdown" class="dropdown-icon" />
              </div>
            </button>

            <div v-if="showRegisterActionsDropdown" class="dropdown-menu">
              <button class="dropdown-option" @click="addRowAndClose">
                <img :src="plusIcon" alt="add" class="option-icon" />
                <span class="option-text">Agregar Fila</span>
              </button>
              <button class="dropdown-option" @click="triggerFileInputAndClose">
                <img :src="excelIcon" alt="file" class="option-icon" />
                <span class="option-text">Cargar desde Archivo</span>
              </button>
            </div>
          </div>

          <input
            id="fileInput"
            type="file"
            ref="fileInput"
            @change="handleFileUpload"
            accept=".xlsx"
            class="hidden-input"
          />
        </div>
        <CustomButton
          :text="buttonText"
          variant="secondary"
          @click="_registerInformation"
          :disabled="disabledValidationButton || getUnregisteredCount() === 0"
          :show-icon="true"
          :icon-src="addUserIcon"
          icon-alt="Agregar"
        />
      </div>
    </div>

    <div v-else-if="activeTab === 'incorporaciones'">
      <IncorporationsList
        :groupId="bulkLoadStore.selectedGroup.id"
        :show-restore-link-button="true"
      />
    </div>

    <div v-else-if="activeTab === 'membresias'">
      <MembershipsTableView :origen="'edit-bulk-load'" />
    </div>

    <div v-else-if="activeTab === 'cobranza'">
      <InvoiceCollections />
    </div>

    <!-- Tab Content -->
    <div v-if="activeTab === 'dashboard'">
      <SummaryView :group-id="bulkLoadStore.selectedGroup.id" />
    </div>

    <div v-else-if="activeTab === 'configuracion'">
      <GroupSettings :show-agent-selector="true" :on-administrators-updated="refreshSummaryPills" />
    </div>
    <div v-if="showModalFinishProccess" class="custom-modal-overlay">
      <div class="custom-modal">
        <span>
          <img :src="modalFinishProccessIcon" width="50" height="50" />
        </span>
        <div class="custom-modal-title" :style="{ color: modalFinishProccessTitleColor }">
          {{ modalFinishProccessTitle }}
        </div>
        <div class="custom-modal-message">{{ modalFinishProccessMessage }}</div>
        <div class="custom-modal-actions">
          <CustomButton text="Permanecer aquí" variant="cancel" @click="handleModalCancel" />
          <CustomButton text="Regresar a inicio" variant="secondary" @click="handleModalAccept" />
        </div>
      </div>
    </div>
    <CustomConfirmationModal
      :show="showModalConfirmation"
      :title="modalConfirmationTitle"
      :message="modalConfirmationMessage"
      :accept-label="modalConfirmationAcceptButtonLabel"
      :sub-message="modalConfirmationSubMessage"
      @accept="handleModalConfirmationAccept(modalConfirmationAction)"
      @cancel="showModalConfirmation = false"
    />
    <ToastMessage
      v-model="showToast"
      :message="toastMessage"
      :submessage="toastSubmessage"
      :duration="10000"
      :type="toastType"
      :icon="toastIcon"
    />
    <div v-if="preRegisterLoading" class="page-loading-overlay">
      <div class="page-loading-content">
        <div class="page-loading-spinner"></div>
        <div class="page-loading-text">{{ loadingProgress || 'Procesando...' }}</div>
      </div>
    </div>

    <div v-if="showDuplicateModal" class="duplicate-modal-overlay">
      <div class="duplicate-modal">
        <div class="duplicate-modal-header">
          <h3>Omitir registros</h3>
        </div>
        <div class="duplicate-modal-body">
          <p class="duplicate-info">
            El archivo contiene [<span class="bold">{{ totalRecords }}</span
            >] colaboradores de los cuales [<span class="bold">{{ duplicateDataCount }}</span
            >] están duplicados
          </p>

          <button class="duplicate-option-btn" @click="handleSkipDuplicates">
            <img src="@/assets/triangle-info-icon.svg" alt="info" width="24" height="24" />
            <span>Omitir colaboradores duplicados</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, inject } from 'vue'
import { useBulkLoadStore } from '../stores/bulkLoad'
import { onBeforeRouteLeave } from 'vue-router'
import { ApiService } from '../services/apiService'
import CustomConfirmationModal from '../components/CustomConfirmationModal.vue'
import { DataValidatorService } from '../../../commons/src/data-validator/services/data-validator.service'
import type {
  MemberColumnInfo,
  MembersData,
} from '../../../commons/src/data-validator/dto/validated-data'
import excelIcon from '@/assets/excel-icon-black.svg'
import { RegisterStatus } from '../../../commons/src/data-validator/dto/register-status-enum'
import plusIcon from '@/assets/plus-icon.svg'
import { MAX_FILE_SIZE, STORAGE_KEY } from '../utils/constants'
import { validateFile } from '../utils/fileValidation'
import { columnMap } from '../utils/constants'
import ToastMessage from '../components/ToastMessage.vue'
import warningIcon from '@/assets/warning-icon.svg'
import successIcon from '@/assets/ok-icon.svg'
import {
  MEMBER_COLUMNS_POSITION,
  MEMBERS_FILE_STRUCTURE,
} from '../../../commons/src/data-validator/dto/members-file-structure'
import { MembersProcess } from './services/members-process'
import EmployeeTable from '../components/EmployeeTable.vue'
import BreadCrumb from '../components/BreadCrumb.vue'
import TabsNavigation from '../components/TabsNavigation.vue'
import { type ApiError } from '../types/errors'
import MembershipsTableView from '../components/MembershipsTableView.vue'
import IncorporationsList from '../onboarding/components/IncorporationsList.vue'
import InvoiceCollections from '../components/InvoiceCollections.vue'
import GroupSettings from '../components/GroupSettings.vue'
import SummaryPills from '../components/SummaryPills.vue'
import checkIcon from '@/assets/check-icon.svg'
import errorIcon from '@/assets/error-icon-white.svg'
import {
  type AgentDTO,
  ColumnType,
  type PreRegisterDataResultDto,
  type CustomerDTO,
  type MigrationResponseDto,
} from 'commons'
import downArrowIcon from '@/assets/down-arrow-black.svg'
import CustomButton from '@/components/CustomButton.vue'
import addUserIcon from '@/assets/add-user.svg'
import SummaryView from '../onboarding/components/SummaryView.vue'

const apiService = inject<ApiService>('apiService')
const dataValidatorService = inject<DataValidatorService>('dataValidatorService')
const membersProcess = inject<MembersProcess>('membersProcess')

if (!apiService) {
  throw new Error('apiService is not provided')
}
if (!membersProcess) {
  throw new Error('membersProcess is not provided')
}
if (!dataValidatorService) {
  throw new Error('dataValidatorService is not provided')
}

const bulkLoadStore = useBulkLoadStore()
const tableData = ref<MembersData[]>([])
const preRegisterLoading = ref(false)
const loadingProgress = ref('')
const fileUploading = ref(false)
const modifiedAfterError = ref<Set<number>>(new Set())
const dirtyCount = computed(() =>
  tableData.value.reduce(
    (acc, row) => acc + row.columns!.filter((col) => col.isDirty === true).length,
    0,
  ),
)
const inconsistenciesCount = computed(() => {
  return tableData.value.reduce((count, row) => {
    return count + (row.columns?.filter((col) => !col.isValid || col.isRepeated).length || 0)
  }, 0)
})
const showOnlyInconsistencies = ref(false)
const showModalFinishProccess = ref(false)
const modalFinishProccessTitle = ref('')
const modalFinishProccessMessage = ref('')
const modalFinishProccessIcon = ref('')
const modalFinishProccessTitleColor = ref('')
const showModalConfirmation = ref(false)
const modalConfirmationMessage = ref('')
const modalConfirmationSubMessage = ref('')
const modalConfirmationTitle = ref('')
const modalConfirmationAcceptButtonLabel = ref('')
const modalConfirmationAction = ref<() => void>(() => {})
const disabledValidationButton = computed(() => {
  return inconsistenciesCount.value > 0
})
const showToast = ref(false)
const toastMessage = ref('')
const toastSubmessage = ref('')
const toastType = ref<'success' | 'error'>('success')
const toastIcon = ref<string | undefined>(undefined)
const retryingRows = ref<Set<number>>(new Set())

const showDuplicateModal = ref(false)
const totalRecords = ref(0)
const duplicateDataCount = ref(0)
const pendingNewRows = ref<MembersData[]>([])
const availableAgents = ref<AgentDTO[]>([])
const selectedAgent = ref('')
const summaryPillsRef = ref()

const activeTab = ref('altas')
const navigationTabs = ref([
  { key: 'altas', label: 'Altas', disabled: false },
  { key: 'incorporaciones', label: 'Incorporaciones', disabled: false },
  { key: 'membresias', label: 'Coberturas', disabled: false },
  { key: 'cobranza', label: 'Cobranza', disabled: false },
  { key: 'dashboard', label: 'Dashboard', disabled: false },
  { key: 'configuracion', label: 'Configuración', disabled: false },
])

const filteredTableData = computed(() => {
  if (!showOnlyInconsistencies.value) return tableData.value
  return tableData.value.filter((row) => rowHasErrors(row))
})

const buttonText = computed(() => {
  const unregisteredCount = tableData.value.filter((row) => {
    const statusColumn = row.columns?.[MEMBER_COLUMNS_POSITION.STATUS]
    return (
      statusColumn?.columnValue === RegisterStatus.NOT_REGISTERED ||
      statusColumn?.columnValue === RegisterStatus.NOT_REGISTERED_EDITED ||
      statusColumn?.columnValue === RegisterStatus.ERROR_EDITED
    )
  }).length

  return `Registrar ${unregisteredCount} colaboradores`
})

const selectedGroupDescription = computed(() => {
  const nombrePublico = bulkLoadStore.publicGroupName
  const descripcion = bulkLoadStore.selectedGroup?.descripcion
  const nombrePublicoText = nombrePublico && nombrePublico !== '' ? ` (${nombrePublico})` : ''
  return `${descripcion}${nombrePublicoText}`
})

const isRowRegistered = (row: MembersData) => {
  if (!row.columns) {
    return false
  }
  const currentStatus = row.columns![MEMBER_COLUMNS_POSITION.STATUS]?.columnValue

  return currentStatus === RegisterStatus.REGISTERED || currentStatus === RegisterStatus.LEGACY
}

const getUnregisteredCount = () => {
  return tableData.value.filter((row) => {
    const statusColumn = row.columns?.find(
      (col) => col.columnName === MEMBERS_FILE_STRUCTURE[MEMBER_COLUMNS_POSITION.STATUS].columnName,
    )
    return (
      statusColumn?.columnValue === RegisterStatus.NOT_REGISTERED ||
      statusColumn?.columnValue === RegisterStatus.NOT_REGISTERED_EDITED ||
      statusColumn?.columnValue === RegisterStatus.ERROR_EDITED
    )
  }).length
}

function goBackWithConfirmation() {
  if (dirtyCount.value > 0) {
    showModalConfirmation.value = true
    modalConfirmationTitle.value = '¿Deseas salir sin completar registro?'
    modalConfirmationMessage.value =
      'Al salir sin registrar, los cambios que realizaste se perderán, esta acción no puede deshacerse'
    modalConfirmationAcceptButtonLabel.value = 'Salir sin registrar'
    modalConfirmationAction.value = goBack
    return
  }
  goBack()
}

function goBack() {
  localStorage.removeItem(STORAGE_KEY)
  window.history.back()
}

const checkCompanyExists = async () => {
  try {
    if (!bulkLoadStore.selectedGroup?.id) {
      return false
    }
    const companyDto = await apiService.getCompanyByBusinessId(
      bulkLoadStore.selectedGroup.id.toString(),
    )
    if (companyDto) {
      return true
    }
    return false
  } catch {
    return false
  }
}

onMounted(async () => {
  availableAgents.value = bulkLoadStore.availableAgents || []
  selectedAgent.value = bulkLoadStore.selectedAgent || ''

  const companyExists = await checkCompanyExists()
  if (!companyExists) {
    const cobranzaTab = navigationTabs.value.find((tab) => tab.key === 'cobranza')
    const configuracionTab = navigationTabs.value.find((tab) => tab.key === 'configuracion')
    if (cobranzaTab) {
      cobranzaTab.disabled = true
    }
    if (configuracionTab) {
      configuracionTab.disabled = true
    }
  }

  const response = await apiService.getMembersByCompanyId(bulkLoadStore.selectedGroup.id)
  tableData.value = JSON.parse(JSON.stringify(response))
  addRegisterStatusColumn()
  membersProcess.countInconsistenciesAndErrors(tableData.value)
  importCoreData()
  if (window.sessionStorage.getItem('showBulkLoadToast')) {
    showToast.value = true
    toastMessage.value = 'Carga de la información'
    toastSubmessage.value = 'se completó con éxito'
    window.sessionStorage.removeItem('showBulkLoadToast')
  }
})

function addRegisterStatusColumn() {
  tableData.value.forEach((row) => {
    if (row.columns === undefined) {
      return
    }
    const existingStatusColumn = row.columns!.find(
      (col) => col.columnName === MEMBERS_FILE_STRUCTURE[MEMBER_COLUMNS_POSITION.STATUS].columnName,
    )

    if (!existingStatusColumn) {
      const registerStatusColumn: MemberColumnInfo = {
        columnName: MEMBERS_FILE_STRUCTURE[MEMBER_COLUMNS_POSITION.STATUS].columnName,
        columnValue: RegisterStatus.NOT_REGISTERED,
        isValid: true,
        isRepeated: false,
        isDirty: false,
        isOriginalRepeated: false,
      }
      row.columns!.push(registerStatusColumn)
    }
  })
}

function updateRowStatus(rowIdx: number, status: string) {
  if (tableData.value[rowIdx]) {
    const statusColumn = tableData.value[rowIdx].columns!.find(
      (col) => col.columnName === MEMBERS_FILE_STRUCTURE[MEMBER_COLUMNS_POSITION.STATUS].columnName,
    )
    if (statusColumn) {
      statusColumn.columnValue = status
    }
  }
}

function updateRowStatusToSuccess(rowIdx: number) {
  updateRowStatus(rowIdx, RegisterStatus.REGISTERED)
}

function updateRowStatusToError(rowIdx: number) {
  updateRowStatus(rowIdx, RegisterStatus.ERROR)
}

function updateRowStatusToNotRegisteredEdited(rowIdx: number) {
  updateRowStatus(rowIdx, RegisterStatus.NOT_REGISTERED_EDITED)
}

function updateRowStatusToErrorEdited(rowIdx: number) {
  updateRowStatus(rowIdx, RegisterStatus.ERROR_EDITED)
}

function getRowStatus(rowIdx: number): string {
  if (tableData.value[rowIdx]) {
    const statusColumn = tableData.value[rowIdx].columns?.find(
      (col) => col.columnName === MEMBERS_FILE_STRUCTURE[MEMBER_COLUMNS_POSITION.STATUS].columnName,
    )
    return statusColumn?.columnValue || RegisterStatus.NOT_REGISTERED
  }
  return RegisterStatus.NOT_REGISTERED
}

onBeforeRouteLeave((to, from, next) => {
  localStorage.removeItem('bulkLoadTableData')
  next()
})

watch(
  tableData,
  (val) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(val))
  },
  { deep: true },
)

function rowHasErrors(row: MembersData) {
  return row.columns?.some((col) => !col.isValid || col.isRepeated)
}

function confirmationBeforeRemoveRow(rowIdx: number) {
  const names = tableData.value[rowIdx].columns?.find(
    (col) =>
      col.columnName === MEMBERS_FILE_STRUCTURE[MEMBER_COLUMNS_POSITION.FIRST_NAME].columnName,
  )?.columnValue
  const paternalLastName = tableData.value[rowIdx].columns?.find(
    (col) =>
      col.columnName === MEMBERS_FILE_STRUCTURE[MEMBER_COLUMNS_POSITION.LAST_NAME].columnName,
  )?.columnValue
  showModalConfirmation.value = true
  modalConfirmationTitle.value = 'Eliminar empleado'
  modalConfirmationMessage.value = `¿Seguro que deseas eliminar el registro de ${names} ${paternalLastName}?`
  modalConfirmationSubMessage.value = 'Esta acción no puede deshacerse.'
  modalConfirmationAcceptButtonLabel.value = 'Si, eliminar'
  modalConfirmationAction.value = () => removeRow(rowIdx)
}

const removeRow = async (idx: number) => {
  try {
    const memberId = tableData.value[idx].columns?.find(
      (col) => col.columnName === MEMBERS_FILE_STRUCTURE[MEMBER_COLUMNS_POSITION.ID].columnName,
    )?.columnValue
    if (memberId) {
      await apiService.deleteMember(memberId)
    }
    tableData.value.splice(idx, 1)
    membersProcess.countInconsistenciesAndErrors(tableData.value)
    toastMessage.value = 'Eliminación exitosa'
    toastSubmessage.value = 'Se ha eliminado el registro de forma exitosa'
    toastType.value = 'success'
    toastIcon.value = successIcon
    showToast.value = true
  } catch (error) {
    const apiError = error as ApiError
    const errorData = await apiError.response.json()
    toastMessage.value = 'Eliminación incorrecta'
    toastSubmessage.value = errorData.message
    toastType.value = 'error'
    toastIcon.value = errorIcon
    showToast.value = true
  }
}

function getFieldErrors(col: MemberColumnInfo): string[] {
  const errors: string[] = []
  if (!col.isValid) errors.push(`${col.columnName} inválido`)
  if (col.isRepeated) errors.push(`${col.columnName} repetido`)
  return errors
}

const handleModalAccept = async () => {
  goBack()
}

const handleModalCancel = () => {
  showModalFinishProccess.value = false
}

const reValidateData = (cell?: MemberColumnInfo) => {
  if (cell) {
    cell.isDirty = true

    const rowIndex = tableData.value.findIndex((row) => row.columns?.some((col) => col === cell))

    if (rowIndex !== -1) {
      const currentStatus =
        tableData.value[rowIndex].columns?.[MEMBER_COLUMNS_POSITION.STATUS]?.columnValue

      if (currentStatus === RegisterStatus.LEGACY) {
        return
      }

      if (currentStatus === RegisterStatus.ERROR) {
        modifiedAfterError.value.add(rowIndex)
        updateRowStatusToErrorEdited(rowIndex)
      } else if (currentStatus === RegisterStatus.NOT_REGISTERED) {
        updateRowStatusToNotRegisteredEdited(rowIndex)
      }
    }
  }

  dataValidatorService.validateData(tableData.value)
  membersProcess.countInconsistenciesAndErrors(tableData.value)
}

function handleModalConfirmationAccept(action: () => void) {
  showModalConfirmation.value = false
  action()
}

const _registerInformation = async () => {
  preRegisterLoading.value = true
  try {
    console.log('_registerInformation - tableData.value:', tableData.value)
    registerInformation(tableData.value)
  } catch (error) {
    console.log('Error in _registerInformation:', error)
    preRegisterLoading.value = false
    showModalFinishProccess.value = true
    modalFinishProccessTitle.value = 'Error en el proceso'
    modalFinishProccessMessage.value =
      'Hubo un error durante el proceso de registro. Por favor, inténtalo de nuevo.'
    modalFinishProccessTitleColor.value = '#d32f2f'
    modalFinishProccessIcon.value = warningIcon
    console.error('Hubo un error: Error calling preRegisterCustomers API ', error)
  } finally {
    retryingRows.value.clear()
  }
}

const registerInformation = async (membersData: MembersData[]) => {
  console.log('registerInformation - membersData:', membersData)
  const customers = mapMembersToCustomers(membersData)

  // processBatch now returns an object: { membersData, successCounter, failureCounter }
  const batchResult = await processBatch(customers)

  // Pass only the membersData array to analyzeResponse which expects MembersData[]
  analyzeResponse(batchResult.membersData, batchResult.successCounter, batchResult.failureCounter)
}

const processBatch = async (
  customers: CustomerDTO[],
): Promise<{ membersData: MembersData[]; successCounter: number; failureCounter: number }> => {
  const batchSize = 10
  const totalBatches = Math.ceil(customers.length / batchSize)
  let allResults: MembersData[] = []
  let successCounter = 0
  let failureCounter = 0

  for (let i = 0; i < customers.length; i += batchSize) {
    const customersBatch = customers.slice(i, i + batchSize)
    const batchNumber = Math.floor(i / batchSize) + 1
    console.log('edit-bulk-load-data bulkLoadStore.selectedAgent', bulkLoadStore.selectedAgent)

    const batchResponse: PreRegisterDataResultDto = await apiService.preRegisterCustomers(
      '/pre-register',
      customersBatch,
      {
        group: bulkLoadStore.publicGroupName,
        groupId: bulkLoadStore.selectedGroup?.id || 0,
        agentKey: bulkLoadStore.selectedAgent,
        groupDescription: bulkLoadStore.selectedGroup?.descripcion || '',
      },
    )
    const percentage = Math.round((batchNumber / totalBatches) * 100)

    loadingProgress.value = `Procesando (${percentage}%)`

    if (percentage === 100) {
      await new Promise((resolve) => setTimeout(resolve, 500))
    }

    allResults = [...allResults, ...batchResponse.membersData]
    successCounter += batchResponse.successRecordsCount
    failureCounter += batchResponse.failedRecordsCount
  }
  return { membersData: allResults, successCounter, failureCounter }
}

const mapMemberToRawCustomer = (memberData: MembersData): Record<string, string> => {
  const rawCustomer: Record<string, string> = {}
  memberData.columns?.forEach((col) => {
    const dtoKey = columnMap[col.columnName]
    if (dtoKey) {
      rawCustomer[dtoKey] = col.columnValue
    }
  })
  return rawCustomer
}

const getApiErrorMessage = (memberData: MembersData): string => {
  const statusColumn = memberData.columns?.find(
    (col) => col.columnName === MEMBERS_FILE_STRUCTURE[MEMBER_COLUMNS_POSITION.STATUS].columnName,
  )
  return statusColumn?.columnDescription || ''
}

const createCustomerFromRaw = (
  rawCustomer: Record<string, string>,
  apiErrorMessage: string,
): CustomerDTO => {
  return {
    id: rawCustomer.ID,
    email: rawCustomer.email?.trim().toLowerCase(),
    names: rawCustomer.names?.toUpperCase().trim() || '',
    paternalLastName: rawCustomer.paternalLastName?.toUpperCase().trim() || '',
    maternalLastName: rawCustomer.maternalLastName?.toUpperCase().trim() || '',
    phone: rawCustomer.phone?.trim() || '',
    productName: rawCustomer.productName,
    taxId: rawCustomer.taxId?.toUpperCase().trim() || '',
    status: rawCustomer.status as RegisterStatus,
    sendEmail: false,
    apiErrorMessage: apiErrorMessage,
    paymentFrequency: rawCustomer.paymentFrequency,
  }
}

const mapMembersToCustomers = (membersData: MembersData[]) => {
  const customers: CustomerDTO[] = membersData.map((memberData) => {
    const rawCustomer = mapMemberToRawCustomer(memberData)
    const apiErrorMessage = getApiErrorMessage(memberData)
    console.log('Mapping memberData to customer - rawCustomer:', rawCustomer)
    return createCustomerFromRaw(rawCustomer, apiErrorMessage)
  })
  return customers
}

const analyzeResponse = (
  response: MembersData[],
  successCounter: number,
  failureCounter: number,
) => {
  preRegisterLoading.value = false

  tableData.value = response
  response.forEach((result, index) => {
    const statusColumn = result?.columns?.[MEMBER_COLUMNS_POSITION.STATUS]

    if (statusColumn?.columnValue === RegisterStatus.REGISTERED) {
      updateRowStatusToSuccess(index)
    } else if (statusColumn?.columnValue === RegisterStatus.ERROR) {
      updateRowStatusToError(index)
    }
  })

  modifiedAfterError.value.clear()

  if (failureCounter > 0) {
    modalFinishProccessTitle.value = 'Errores en el registro'
    modalFinishProccessIcon.value = warningIcon
    modalFinishProccessMessage.value = `Se enviaron ${successCounter + failureCounter} colaborador(es) a procesar, de los cuales ${failureCounter} colaborador(es) no pudieron ser registrados.`
    modalFinishProccessTitleColor.value = '#d32f2f'
  } else {
    modalFinishProccessTitle.value = 'Registro Exitoso'
    modalFinishProccessIcon.value = successIcon
    modalFinishProccessMessage.value = `Se han registrado ${successCounter} colaboradores.`
    modalFinishProccessTitleColor.value = '#6dbc24'
  }
  showModalFinishProccess.value = true
  // socket.emit('leaveGroup', bulkLoadStore.selectedGroup?.id)
}

const addRow = () => {
  const firstRow = {
    columns: [],
  } as MembersData
  const columnsInfo: MemberColumnInfo[] = []
  MEMBERS_FILE_STRUCTURE.forEach((field) => {
    if (field.isVisible) {
      let defaultValue = ''

      if (field.columnName === MEMBERS_FILE_STRUCTURE[MEMBER_COLUMNS_POSITION.STATUS].columnName) {
        defaultValue = RegisterStatus.NOT_REGISTERED
      } else if (
        field.columnName === MEMBERS_FILE_STRUCTURE[MEMBER_COLUMNS_POSITION.LAST_NAME].columnName ||
        field.columnName ===
          MEMBERS_FILE_STRUCTURE[MEMBER_COLUMNS_POSITION.LAST_NAME_MOTHER].columnName ||
        field.columnName ===
          MEMBERS_FILE_STRUCTURE[MEMBER_COLUMNS_POSITION.FIRST_NAME].columnName ||
        field.columnName === MEMBERS_FILE_STRUCTURE[MEMBER_COLUMNS_POSITION.RFC].columnName
      ) {
        defaultValue = '' // Will be converted to uppercase on input
      }

      columnsInfo.push({
        columnName: field.columnName,
        columnValue: defaultValue,
        isValid: true,
        isRepeated: false,
        isDirty: false,
        isOriginalRepeated: false,
        type: field.type,
        isVisible: field.isVisible,
        isRequiredInFile: field.isRequiredInFile,
      })
    }
  })
  firstRow.columns?.push(...columnsInfo)
  tableData.value.push(firstRow)
  dataValidatorService.validateData(tableData.value)
  membersProcess.countInconsistenciesAndErrors(tableData.value)
}

const fileInput = ref<HTMLInputElement | null>(null)

const triggerFileInput = () => {
  fileInput.value?.click()
}

const handleFileUpload = async (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files.length > 0) {
    fileUploading.value = true
    const file = target.files[0]

    try {
      await validateFile(file, MAX_FILE_SIZE, ['xlsx'])

      const newMembers = await apiService.sendFile<MembersData[]>('/file-loader', file)

      dataValidatorService.validateData(newMembers)

      const actualMembers = tableData.value

      const processResponse = membersProcess.mergeMembers(newMembers, actualMembers)

      if (processResponse.duplicatedDataCount > 0) {
        showDuplicateModal.value = true
        totalRecords.value = newMembers.length
        duplicateDataCount.value = processResponse.duplicatedDataCount
        pendingNewRows.value = processResponse.newRows
      } else {
        tableData.value = processResponse.allData
        membersProcess.countInconsistenciesAndErrors(tableData.value)
      }
    } catch (error) {
      const apiError = error as ApiError
      const errorData = await apiError.response.json()
      toastMessage.value = 'Error'
      toastSubmessage.value = errorData.message
      toastType.value = 'error'
      toastIcon.value = errorIcon
      showToast.value = true
    } finally {
      fileUploading.value = false
    }

    target.value = ''
  }
}

const handleSkipDuplicates: () => void = () => {
  showDuplicateModal.value = false
  if (pendingNewRows.value.length > 0) {
    tableData.value = [...tableData.value, ...pendingNewRows.value]
    dataValidatorService.validateData(tableData.value)
    membersProcess.countInconsistenciesAndErrors(tableData.value)
    pendingNewRows.value = []
  }
}

const handleTabChange = (tabKey: string) => {
  const tab = navigationTabs.value.find((t) => t.key === tabKey)
  if (tab && !tab.disabled) {
    activeTab.value = tabKey
  }
}

// Expose reactive refs for testing purposes
defineExpose({
  showDuplicateModal,
  totalRecords,
  duplicateDataCount,
  pendingNewRows,
})

const getCabeceras = () => {
  const columnsInfo: MemberColumnInfo[] = []
  MEMBERS_FILE_STRUCTURE.forEach((field) => {
    if (field.isVisible) {
      columnsInfo.push({
        columnName: field.columnName,
        columnValue: '',
        isValid: true,
        isRepeated: false,
        type: field.type,
        isVisible: field.isVisible,
        isRequiredInFile: field.isRequiredInFile,
      })
    }
  })
  return columnsInfo
}

const saveUpdatedField = async (
  employee: MembersData,
  field: ColumnType,
  columnName?: string,
): Promise<boolean> => {
  try {
    let fieldValue = employee.columns?.find((col) => col.type === field)?.columnValue
    if (field === ColumnType.TEXT) {
      fieldValue = employee.columns?.find((col) => col.columnName === columnName)?.columnValue
    }
    if (field === ColumnType.EMAIL) {
      fieldValue = fieldValue?.trim().toLowerCase()
    }
    await apiService.updateMemberEmail(
      employee.columns![MEMBER_COLUMNS_POSITION.ID]?.columnValue,
      fieldValue!,
      field,
      columnName,
    )
    showToast.value = true
    toastMessage.value = 'Modificación exitosa'
    toastSubmessage.value = 'Se modificó existosamente el valor'
    toastType.value = 'success'
    toastIcon.value = checkIcon
    return true
  } catch (error) {
    const apiError = error as ApiError
    const errorData = await apiError.response.json()
    toastMessage.value = 'Error'
    toastSubmessage.value = errorData.message
    toastType.value = 'error'
    toastIcon.value = errorIcon
    showToast.value = true
    return false
  }
}

const showRegisterActionsDropdown = ref(false)

const toggleRegisterActionsDropdown = () => {
  showRegisterActionsDropdown.value = !showRegisterActionsDropdown.value
}

const handleClickOutside = (event: Event) => {
  const target = event.target as HTMLElement
  if (!target.closest('.register-actions-container')) {
    showRegisterActionsDropdown.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

const addRowAndClose = () => {
  addRow()
  showRegisterActionsDropdown.value = false
}

const triggerFileInputAndClose = () => {
  triggerFileInput()
  showRegisterActionsDropdown.value = false
}

const refreshSummaryPills = async () => {
  if (summaryPillsRef.value?.refreshAdministrators) {
    await summaryPillsRef.value.refreshAdministrators()
  }
}

const importCoreData = async () => {
  if (bulkLoadStore.selectedGroup?.id && bulkLoadStore.executeMigration) {
    try {
      preRegisterLoading.value = true
      loadingProgress.value = `Importando datos...`
      const result: MigrationResponseDto = await apiService.migrateLegacyGroups([
        bulkLoadStore.selectedGroup.id,
      ])
      const { employeesCreated, employeesUpdated } = result.summary
      toastMessage.value = 'Importación exitosa'

      if (employeesCreated > 0 && employeesUpdated > 0) {
        toastSubmessage.value = `Se importaron ${employeesCreated} registros y se actualizaron ${employeesUpdated}.`
      } else if (employeesCreated > 0) {
        toastSubmessage.value = `Se importaron ${employeesCreated} registros exitosamente.`
      } else if (employeesUpdated > 0) {
        toastSubmessage.value = `Se actualizaron ${employeesUpdated} registros.`
      } else {
        toastSubmessage.value = `No se encontraron registros nuevos para importar.`
      }

      toastType.value = 'success'
      toastIcon.value = checkIcon
      showToast.value = true
    } catch {
      showToast.value = true
      toastMessage.value = 'Error en la importación'
      toastSubmessage.value = 'Hubo un error durante la importación de datos'
      toastType.value = 'error'
      toastIcon.value = errorIcon
    } finally {
      preRegisterLoading.value = false
      bulkLoadStore.setExecuteMigration(false)
    }
  }
}
</script>

<style scoped>
:global(html),
:global(body) {
  background: #fff !important;
}
.back-row {
  margin-bottom: 2.5rem;
}
.back-btn {
  background: none;
  border: none;
  color: #0019a5;
  font-size: 1.3rem;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0;
}
.back-arrow {
  font-size: 2rem;
  color: #0019a5;
  font-weight: bold;
  vertical-align: middle;
}
.back-text {
  color: #0019a5;
  font-size: 1.1rem;
  font-weight: bold;
}

.tabs-navigation {
  margin-bottom: 2.5rem;
}
.summary-bar-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 1.5rem;
  gap: 1rem;
}

.left-section {
  display: flex;
  align-items: center;
}

.middle-section {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 2rem;
}

.right-section {
  display: flex;
  align-items: center;
  margin-left: auto;
}

/* Removed summary-bar-wrapper as it's no longer needed */
.summary-bar {
  display: flex;
  gap: 2rem;
  font-weight: bold;
}
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 22px;
}
.slider:before {
  position: absolute;
  content: '';
  height: 16px;
  width: 16px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}
input:checked + .slider {
  background-color: #003896;
}
input:checked + .slider:before {
  transform: translateX(18px);
}

input:disabled + .slider {
  background-color: #e0e0e0;
  cursor: not-allowed;
}

input:disabled + .slider:before {
  background-color: #bdbdbd;
}
.members-count {
  color: #333;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  font-weight: bold;
}
.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #003896;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
.validate-btn {
  background: #003896;
  color: #fff;
  border: none;
  border-radius: 5px;
  padding: 10px 20px;
  font-size: 1rem;
  cursor: pointer;
  margin-top: 1rem;
  transition: all 0.3s ease;
}

.validate-btn:disabled {
  background: #ccc;
  color: #666;
  cursor: not-allowed;
  opacity: 0.6;
}
.validate-btn-wrapper {
  display: flex;
  justify-content: flex-end;
}

.header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  position: relative;
}
/* Removed add-file-btn-wrapper as it's no longer needed */
.header-row .back-btn {
  position: relative;
  z-index: 1;
}
.red-text {
  color: #d32f2f;
  font-weight: bold;
}
.custom-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.custom-modal {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.2);
  padding: 2rem 2.5rem;
  min-width: 320px;
  width: 35vw;
  max-width: 90vw;
  text-align: center;
}
.custom-modal-title {
  font-size: 1.3rem;
  font-weight: bold;
  margin-bottom: 1rem;
  text-align: center;
}
.custom-modal-message {
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
  color: #333;
}
.required-message {
  color: #d32f2f;
  font-size: 0.95rem;
  margin-top: 0.2rem;
  display: block;
}
.required-message.right {
  margin-left: 1rem;
  margin-top: 0;
  display: inline-block;
  white-space: nowrap;
  color: #d32f2f;
  font-size: 0.95rem;
  position: static;
}
.btn-wrapper-row {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-top: 1.5rem;
}

.btn-wrapper-row.single-button {
  justify-content: flex-end;
}

.left-buttons {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.hidden-input {
  display: none;
}

/* Duplicate records modal styles */
.duplicate-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
}

.duplicate-modal {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.2);
  padding: 2rem 2.5rem;
  min-width: 320px;
  width: 35vw;
  max-width: 90vw;
  text-align: center;
}

.duplicate-modal-header {
  margin-bottom: 1.5rem;
}

.duplicate-modal-header h3 {
  font-size: 1.3rem;
  font-weight: bold;
  color: #333;
}

.duplicate-modal-body {
  margin-bottom: 1.5rem;
}

.duplicate-info {
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 1rem;
  text-align: left;
}

.duplicate-option {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  background: #f0f0f0;
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 0.8rem 1.2rem;
  font-size: 0.9rem;
  color: #333;
  font-weight: 600;
}

.duplicate-option img {
  width: 24px;
  height: 24px;
}

.duplicate-option-btn {
  background: #fff;
  color: #1a1a1a;
  border: none;
  border-radius: 8px;
  padding: 0.8rem 1.5rem;
  font-size: 1rem;
  cursor: pointer;
  transition:
    background-color 0.3s ease,
    color 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
}

.duplicate-option-btn:hover {
  background: #f5f5f5;
  color: #003896;
}

.bold {
  font-weight: bold;
}

/* Import Database Button Styling */
.import-database-btn {
  background: #003896 !important;
  color: #ffffff !important;
  border-radius: 30px !important;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.15) !important;
  font-family: 'Montserrat', sans-serif !important;
  font-weight: 500 !important;
  font-size: 16px !important;
  line-height: 140% !important;
  padding: 16px !important;
  gap: 4px !important;
  width: 249px !important;
  height: 40px !important;
  display: flex !important;
  flex-direction: row !important;
  justify-content: center !important;
  align-items: center !important;
  white-space: nowrap !important;
  text-overflow: ellipsis !important;
  overflow: hidden !important;
}

.import-database-btn :deep(.button-text) {
  width: 189px !important;
  height: 22px !important;
  font-family: 'Montserrat', sans-serif !important;
  font-style: normal !important;
  font-weight: 500 !important;
  font-size: 16px !important;
  line-height: 140% !important;
  text-align: center !important;
  color: #ffffff !important;
  flex: none !important;
  order: 1 !important;
  flex-grow: 0 !important;
  white-space: nowrap !important;
}

.import-database-btn:hover {
  background: #003896 !important;
  color: #ffffff !important;
}

.import-database-btn:focus {
  background: #003896 !important;
  color: #ffffff !important;
}

.register-actions-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 7px;
  width: 250px;
  height: 48px;
}

.register-actions-button {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  gap: 8px;
  width: 250px;
  height: 48px;
  background: #9cd41c;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.15);
  border-radius: 30px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  overflow: hidden;
}

.register-actions-button:hover {
  background: #8bc41a;
}

.register-actions-button.active {
  background: #8bc41a;
}

.button-text {
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  text-align: left;
  color: #1a1a1a;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.icon-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.separator-line {
  width: 1px;
  height: 24px;
  background: #000000;
  flex-shrink: 0;
}

.dropdown-icon {
  width: 18px;
  height: 9px;
  flex-shrink: 0;
  transition: transform 0.3s ease;
}

.register-actions-button.active .dropdown-icon {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  bottom: 100%;
  left: 0;
  z-index: 1000;
  background: #ffffff;
  border: 1px solid #d5d5d5;
  border-radius: 8px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  width: 250px;
  margin-bottom: 5px;
}

.dropdown-option {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 10px 8px;
  gap: 8px;
  width: 100%;
  height: 44px;
  background: #ffffff;
  border: none;
  border-bottom: 2px solid #d5d5d5;
  cursor: pointer;
  transition: background-color 0.2s ease;
  justify-content: flex-start;
}

.dropdown-option:last-child {
  border-bottom: none;
}

.dropdown-option:hover {
  background: #f5f5f5;
}

.option-icon {
  width: 24px;
  height: 24px;
  flex: none;
  order: 0;
  flex-grow: 0;
}

.option-text {
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 150%;
  color: #1a1a1a;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}

/* Tabs and Summary Row */
.tabs-and-summary-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 0px;
  width: 100%;
  gap: 0px;
}

.employee-table-container {
  margin-top: 24px;
}

.tabs-wrapper {
  flex: 0 0 auto;
}

.tabs-wrapper :deep(.tabs-navigation) {
  width: auto;
}

.summary-pills-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-top: 0.5rem;
}

/* Inconsistencies Section - Positioned on the right side */
.inconsistencies-section {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.inconsistencies-section .summary-bar {
  display: flex;
  gap: 0;
  font-weight: bold;
}

.inconsistencies-section .toggle-button {
  display: flex;
  align-items: flex-end;
}

/* Inconsistencies text styling */
.inconsistencies-text-bold {
  font-weight: bold;
}

.inconsistencies-text-normal {
  font-weight: normal;
}
</style>
